<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <div ref="root" class="attr-image"
            v-wjTooltip="attr.tooltip"
            v-wjContextMenu="'ContextWidgetMenuId'"
        >
            <label class="label-attr" v-if="attr.title && !attr.hideTitle" :for="attr.html_id">{{ attr.title }}</label>

            <!-- IE 2026 09 23 / ODOO-341407: i18n -->
            <div class="attr-image-toolbar" v-if="src && editing">
                <button class="ai-color-swatch" :style="{ background: rectColor }" @click="openColorPicker" :title="t('Color')" />
                <button class="ai-btn" @click="undoRect" :title="t('Undo')"><i class="pi pi-undo"></i></button>
            </div>
            <wj-color-picker style="display:none;position:fixed;z-index:1000" :initialized="colorPickerInit" />

            <div ref="wrap" class="attr-image-wrap" :style="wrapStyle" :class="{ editing }"
                @click="!editing && openFull()"
            >
                <img ref="img" v-if="src" :src="src" class="attr-image-img" :style="imgStyle"
                    @load="onImgLoad"
                    draggable="false"
                />
                <canvas v-if="editing" ref="canvas" class="attr-image-canvas"
                    @mousedown.prevent="onMouseDown"
                    @mousemove="onMouseMove"
                    @mouseup="onMouseUp"
                    @mouseleave="onMouseUp"
                />
                <div v-else-if="!src" class="attr-image-empty"><i class="pi pi-image" /></div>
            </div>
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'

export default {
    props: ['widget_id', 'attr_id'],
    data: () => ({
        WIDGET: {},
        src: '',
        srcOriginal: '',
        editing: true,
        rectColor: '#ff3b30bf',
        rectWidth: 6,
        rects: [],
        dragState: null,
        colorPicker: null,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.content_Changed()
    },
    mounted() {
        this.WIDGET.attrsResize = this.WIDGET.attrsResize || []
        this.WIDGET.attrsResize.push(this.attrResize)
        this.$nextTick(() => this.attrResize())
        this.resizeObserver = new ResizeObserver(() => {
            if (this.editing) this._syncCanvas()
        })
        this.$nextTick(() => {
            if (this.$refs.wrap) this.resizeObserver.observe(this.$refs.wrap)
        })
    },
    beforeUnmount() {
        this.resizeObserver?.disconnect()
        if (this.WIDGET.attrsResize) {
            const idx = this.WIDGET.attrsResize.indexOf(this.attrResize)
            if (idx !== -1) this.WIDGET.attrsResize.splice(idx, 1)
        }
    },
    methods: {
        colorPickerInit(cp) {
            const blurEvt = /firefox/i.test(navigator.userAgent) ? 'blur' : 'focusout'
            cp.hostElement.addEventListener(blurEvt, () => {
                window.$cg.setTimeout(this, 'AttrImage.colorPicker', () => {
                    if (!cp.containsFocus()) cp.hostElement.style.display = 'none'
                }, 0)
            })
            cp.valueChanged.addHandler(() => {
                this.rectColor = cp.value
            })
            this.colorPicker = cp
        },
        openColorPicker(event) {
            const he = this.colorPicker?.hostElement
            if (!he) return
            const { left, bottom } = event.target.getBoundingClientRect()
            this.colorPicker.value = this.rectColor
            he.style.left = left + 'px'
            he.style.top  = bottom + 'px'
            he.style.display = 'inline'
            he.focus()
        },
        attrResize() {
            if (this.attrResize_timeout) clearTimeout(this.attrResize_timeout)
            this.attrResize_timeout = window.$cg.setTimeout(this, 'AttrImage.attrResize', () => {
                this.attrResize_timeout = null
                this.attrResize_afterTimeout()
            }, 1)
        },
        attrResize_afterTimeout() {
            if (this.attr?.noAutoResize) return
            const wrapEl = this.$refs.wrap
            if (!wrapEl) return
            const wrapRect = wrapEl.getBoundingClientRect()
            if (wrapRect.height <= 10 && !this.src) return
            const elementForm = document.getElementById(`WIDGET${this.widget_id}`)
            if (!elementForm) return
            const widgetAttrsEl = elementForm.querySelector('.widget-attrs')
            const containerRect = (widgetAttrsEl || elementForm).getBoundingClientRect()
            let newHeight = Math.floor(containerRect.bottom - wrapRect.top - 3)
            if (newHeight > containerRect.height) newHeight = 300
            if (newHeight < 80) newHeight = 80
            if (wrapEl.style.height !== `${newHeight}px`) {
                wrapEl.style.height = `${newHeight}px`
                if (this.editing) this._syncCanvas()
            }
        },
        content_Changed() {
            this.store.widget_attr_set(this)
            this.src = this.store.attr_get(this.WIDGET, this.attr) || ''
        },
        openFull() {
            if (!this.src) return
            const win = window.open()
            win.document.write(`<img src="${this.src}" style="max-width:100%;max-height:100vh;" />`)
        },
        onImgLoad() {
            if (this.editing) this._syncCanvas()
        },
        toggleEdit() {
            this.editing = !this.editing
            if (this.editing) {
                this.srcOriginal = this.src
                this.rects = []
                this.$nextTick(() => this._syncCanvas())
            } else {
                this.rects = []
                this.dragState = null
            }
        },
        cancelEdit() {
            this.src = this.srcOriginal
            this.store.attr_set(this.WIDGET, this.attr, this.srcOriginal)
            this.editing = false
            this.rects = []
            this.dragState = null
        },
        undoRect() {
            this.rects.pop()
            this._redraw()
        },

        // Actual rendered pixel area of the image inside the element (object-fit: contain)
        _renderBounds() {
            const img = this.$refs.img
            const el = img?.getBoundingClientRect()
            if (!img || !el || !img.naturalWidth) return null
            const na = img.naturalWidth / img.naturalHeight
            const da = el.width / el.height
            if (da > na) {
                const rw = el.height * na
                return { left: el.left + (el.width - rw) / 2, top: el.top, width: rw, height: el.height }
            } else {
                const rh = el.width / na
                return { left: el.left, top: el.top + (el.height - rh) / 2, width: el.width, height: rh }
            }
        },
        // canvas px (relative to render bounds top-left) → image natural px
        _toImg(cx, cy) {
            const rb = this._renderBounds()
            const img = this.$refs.img
            if (!rb || !img) return { x: cx, y: cy }
            return { x: cx * img.naturalWidth / rb.width, y: cy * img.naturalHeight / rb.height }
        },
        // image natural px → canvas px (relative to render bounds top-left)
        _toCanvas(px, py) {
            const rb = this._renderBounds()
            const img = this.$refs.img
            if (!rb || !img) return { x: px, y: py }
            return { x: px * rb.width / img.naturalWidth, y: py * rb.height / img.naturalHeight }
        },
        _canvasCoords(e) {
            const rb = this._renderBounds()
            if (!rb) return { x: 0, y: 0 }
            return { x: e.clientX - rb.left, y: e.clientY - rb.top }
        },
        _syncCanvas() {
            const canvas = this.$refs.canvas
            const rb = this._renderBounds()
            if (!canvas || !rb) return
            canvas.width  = rb.width
            canvas.height = rb.height
            this._redraw()
        },

        onMouseDown(e) {
            const p = this._canvasCoords(e)
            const pi = this._toImg(p.x, p.y)
            this.dragState = { x0: pi.x, y0: pi.y, x1: pi.x, y1: pi.y,
                color: this.rectColor, width: this.rectWidth }
        },
        onMouseMove(e) {
            if (!this.dragState) return
            const p = this._canvasCoords(e)
            const pi = this._toImg(p.x, p.y)
            this.dragState.x1 = pi.x
            this.dragState.y1 = pi.y
            this._redraw(this.dragState)
        },
        onMouseUp() {
            if (!this.dragState) return
            const r = this.dragState
            this.dragState = null
            const c0 = this._toCanvas(r.x0, r.y0), c1 = this._toCanvas(r.x1, r.y1)
            if (Math.abs(c1.x - c0.x) > 3 || Math.abs(c1.y - c0.y) > 3) {
                this.rects.push({ ...r })
                this._liveApply()
            }
            this._redraw()
        },

        _strokeRoundRect(ctx, x, y, w, h, r) {
            ctx.beginPath()
            ctx.roundRect(x, y, w, h, r)
            ctx.stroke()
        },
        // rects stored in natural image px; drawn via _toCanvas for correct letterbox offset
        _redraw(preview = null) {
            const canvas = this.$refs.canvas
            if (!canvas) return
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            const draw = (r) => {
                const c0 = this._toCanvas(r.x0, r.y0)
                const c1 = this._toCanvas(r.x1, r.y1)
                const cw = c1.x - c0.x, ch = c1.y - c0.y
                ctx.strokeStyle = r.color
                ctx.lineWidth = r.width
                this._strokeRoundRect(ctx, c0.x, c0.y, cw, ch, Math.min(8, Math.abs(cw) / 4, Math.abs(ch) / 4))
            }
            this.rects.forEach(draw)
            if (preview) draw(preview)
        },

        _flatten() {
            const img = this.$refs.img
            if (!img || !this.rects.length) return null
            const rb = this._renderBounds()
            if (!rb) return null
            const scale = img.naturalWidth / rb.width
            const canvas = document.createElement('canvas')
            canvas.width  = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            this.rects.forEach(r => {
                const w = r.x1 - r.x0, h = r.y1 - r.y0
                ctx.strokeStyle = r.color
                ctx.lineWidth = r.width * scale
                this._strokeRoundRect(ctx, r.x0, r.y0, w, h,
                    Math.min(8 * scale, Math.abs(w) / 4, Math.abs(h) / 4))
            })
            return canvas.toDataURL('image/png')
        },
        _liveApply() {
            const url = this._flatten()
            if (!url) return
            this.store.attr_set(this.WIDGET, this.attr, url)
        },
        applyAnnotations() {
            const url = this._flatten()
            if (url) this.src = url
            this.editing = false
            this.rects = []
            this.dragState = null
        },
    },
    computed: {
        wrapStyle() {
            const h = this.attr?.height
            if (!h) return {}
            return { height: typeof h === 'number' ? h + 'px' : h }
        },
        imgStyle() {
            return { objectFit: this.attr.objectFit || 'contain' }
        },
    },
}
</script>

<style scoped>
.attr-image-toolbar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 0 4px;
}
.ai-btn {
    background: none;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 2px 6px;
    cursor: pointer;
    color: #555;
    line-height: 1.6;
}
.ai-btn:hover  { background: #f0f0f0; }
.ai-btn.active { background: #e0f0ff; border-color: #4a90d9; color: #1a6bb5; }
.ai-color-swatch {
    width: 26px;
    height: 26px;
    border: 1px solid #bbb;
    border-radius: 4px;
    cursor: pointer;
    padding: 0;
}
.ai-range {
    width: 64px;
    cursor: pointer;
}
.attr-image-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 80px;
    overflow: hidden;
}
.attr-image-wrap:not(.editing) { cursor: zoom-in; }
.attr-image-img {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    user-select: none;
}
.attr-image-canvas {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    cursor: crosshair;
    pointer-events: all;
}
.attr-image-empty {
    color: #ccc;
    font-size: 2rem;
}
</style>
